import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  Palette,
  simpleHeader,
} from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { dateCell, percentageCell, priceCell } from "src/components";
import { DotIndicator, DotIndicatorProps } from "src/components/DotIndicator";
import { CollapseRowToggle } from "src/components/formFields/CollapseRowToggle";
import { CommitmentStatus } from "src/generated/graphqlTypes";
import {
  createBudgetUrl,
  createCommitmentChangeOrderUrl,
  createCommitmentUrl,
  createReleaseToTradesConfirmUrl,
} from "src/RouteUrls";
import {
  CommittedAtStartTab_ProjectCostCodeDataFetch,
  CommittedAtStartTab_ProjectCostCodeResponse,
  CommittedAtStartTabMappedProjectCostCode,
} from "./CommittedAtStartTab";
import {
  CommittedAtStartTab_CommitmentsByProjectCostCode_CommitmentLikeFragment,
  CommittedAtStartTab_ProjectCostCodeFragment,
  CommittedAtStartTab_ProjectFragment,
} from "./CommittedAtStartTab.generated";

type CommittedAtStartPerProjectTableProps = {
  data: CommittedAtStartTabMappedProjectCostCode[];
  projectCostCodeData: CommittedAtStartTab_ProjectCostCodeResponse;
  fetchProjectCostCodeData: CommittedAtStartTab_ProjectCostCodeDataFetch;
  search?: string;
  commitmentStatusFilter?: CommitmentStatus[];
  showReleased: boolean;
};

export function CommittedAtStartPerProjectTable({
  data,
  projectCostCodeData,
  fetchProjectCostCodeData,
  search,
  commitmentStatusFilter,
  showReleased,
}: CommittedAtStartPerProjectTableProps) {
  const rows = useMemo(
    () => createRows(data, projectCostCodeData, showReleased, commitmentStatusFilter),
    [data, projectCostCodeData, showReleased, commitmentStatusFilter],
  );
  const columns = useMemo(
    () => createColumns(fetchProjectCostCodeData, commitmentStatusFilter),
    [fetchProjectCostCodeData, commitmentStatusFilter],
  );

  return (
    <div css={Css.mt2.$}>
      <GridTable
        rows={rows}
        columns={columns}
        stickyHeader
        style={{ allWhite: true, grouped: true }}
        sorting={{ on: "client" }}
        filter={search}
      />
    </div>
  );
}

enum CostCodeCommittedStatus {
  Missing = "Missing",
  PartiallyReleased = "Partially Released",
  Released = "Released",
  Unknown = "Unknown",
}

type HeaderRow = { kind: "header" };
type CommitmentLikeRowData = {
  name: string;
  committedCostInCents: number;
  desiredValueInCents: number; // The desired value is used to determine the color of the cell
  projectId: string;
  statusDetail: { code: CommitmentStatus; name: string };
};
type CommitmentChangeOrderRow = { kind: "changeOrder"; id: string; data: CommitmentLikeRowData };
type CommitmentRow = {
  kind: "commitment";
  id: string;
  data: CommitmentLikeRowData;
  children: GridDataRow<CommitmentChangeOrderRow>[];
};
type ProjectCostCodeRow = {
  kind: "projectCostCode";
  id: string;
  data: CommittedAtStartTab_ProjectCostCodeFragment & { status: CostCodeCommittedStatus };
  children: GridDataRow<CommitmentRow>[];
};
type SellableProjectRow = {
  kind: "sellableProject";
  data: CommittedAtStartTab_ProjectFragment & { currentCommittedCostInBasisPoints: number };
  children: GridDataRow<ProjectCostCodeRow>[];
};
type TotalsRow = {
  kind: "totals";
  id: string;
  data: { committedAtStartInBasisPoints: number; currentCommittedCostInBasisPoints: number };
};
type CommittedAtStartTableRow =
  | HeaderRow
  | TotalsRow
  | SellableProjectRow
  | ProjectCostCodeRow
  | CommitmentRow
  | CommitmentChangeOrderRow;

function createColumns(
  fetchProjectCostCodeData: CommittedAtStartTab_ProjectCostCodeDataFetch,
  commitmentStatusFilter?: CommitmentStatus[],
): GridColumn<CommittedAtStartTableRow>[] {
  return [
    collapseColumn<CommittedAtStartTableRow>({
      projectCostCode: (_, { row, api }) => ({
        content: (
          <CollapseRowToggle
            api={api}
            label=""
            rowId={row.id}
            onCollapse={async () => {
              const collapsedRows = api
                .getVisibleRows()
                .filter((r) => r.kind === "projectCostCode" && !api.isCollapsedRow(r.id));

              // Fetch project cost code data when the row is expanded
              await fetchProjectCostCodeData({
                variables: {
                  projectCostCodes: collapsedRows.map((r) => r.id),
                  commitmentStatus: commitmentStatusFilter,
                },
              });
            }}
          />
        ),
      }),
      // Commitment Change Order should always be expanded
      changeOrder: emptyCell,
      w: "40px",
    }),
    column<CommittedAtStartTableRow>({
      totals: "Totals",
      header: "Project",
      sellableProject: ({ name }) => name,
      projectCostCode: (pcc) => ({
        content: (
          <Link css={Css.sm.$} to={`${createBudgetUrl(pcc.project.id)}?search=${pcc.costCode.displayName}`}>
            {pcc.costCode.displayName}
          </Link>
        ),
        value: pcc.costCode.displayName,
      }),
      commitment: (commitment, { row }) => ({
        content: (
          <Link css={Css.sm.$} to={createCommitmentUrl(commitment.projectId, row.id)}>
            {commitment.name}
          </Link>
        ),
        value: commitment.name,
      }),
      changeOrder: (cco, { row }) => ({
        content: (
          <Link css={Css.xs.$} to={createCommitmentChangeOrderUrl(cco.projectId, row.id)}>
            {cco.name}
          </Link>
        ),
        value: cco.name,
      }),
    }),
    column<CommittedAtStartTableRow>({
      totals: emptyCell,
      header: "Start Date",
      sellableProject: ({ startDate }) =>
        dateCell(startDate, { dateFormatStyle: "medium", xss: Css.fwb.color("black").$ }),
      projectCostCode: emptyCell,
      commitment: emptyCell,
      changeOrder: emptyCell,
      w: "150px",
    }),
    // This column is used to add a spacing between 'Revised Budget' and 'Status'
    column<CommittedAtStartTableRow>({
      totals: emptyCell,
      header: emptyCell,
      sellableProject: emptyCell,
      projectCostCode: emptyCell,
      commitment: emptyCell,
      changeOrder: emptyCell,
      w: "80px",
    }),
    column<CommittedAtStartTableRow>({
      totals: emptyCell,
      header: {
        content: "Status",
        tooltip:
          "Determined by comparing committed cost to revised cost: 'Missing' if committed is $0, 'Partially Released' if committed is less than revised, 'Released' if committed equals revised.",
      },
      sellableProject: emptyCell,
      projectCostCode: (pcc) => projectCostCodeCommittedStatusCell(pcc),
      commitment: (c) => commitmentLikeStatusCell(c),
      changeOrder: (cco) => commitmentLikeStatusCell(cco),
      clientSideSort: false,
    }),
    numericColumn<CommittedAtStartTableRow>({
      header: {
        content: "Committed at Start",
        tooltip: "The ratio of committed to PO-Eligible cost codes at the Ready to Start milestone. Above 95% = green.",
      },
      totals: (t) => percentageCellInBasisPoints(t.committedAtStartInBasisPoints),
      sellableProject: (p) => percentageCellInBasisPoints(p.committedCostCodesAtStartInBasisPoints ?? 0),
      projectCostCode: (pcc) =>
        priceCell({ valueInCents: pcc.committedAtStartInCents, desiredValueInCents: pcc.startCostInCents }),
      commitment: emptyCell,
      changeOrder: emptyCell,
    }),
    numericColumn<CommittedAtStartTableRow>({
      totals: emptyCell,
      header: "Revised Budget",
      sellableProject: ({ directCostInCents }) => priceCell({ valueInCents: directCostInCents }),
      projectCostCode: ({ revisedCostInCents }) => priceCell({ valueInCents: revisedCostInCents }),
      commitment: emptyCell,
      changeOrder: emptyCell,
    }),
    numericColumn<CommittedAtStartTableRow>({
      header: {
        content: "Current Committed",
        tooltip:
          "The current % of PO-eligible cost codes that are committed vs not. (This includes Cost Codes that are hidden from view but are already resolved).",
      },
      totals: (t) => percentageCellInBasisPoints(t.currentCommittedCostInBasisPoints),
      sellableProject: (p) => percentageCellInBasisPoints(p.currentCommittedCostInBasisPoints),
      projectCostCode: (pcc) =>
        priceCell({ valueInCents: pcc.committedCostInCents, desiredValueInCents: pcc.revisedCostInCents }),
      commitment: ({ committedCostInCents, desiredValueInCents }) =>
        priceCell({ valueInCents: committedCostInCents, desiredValueInCents }),
      changeOrder: ({ committedCostInCents, desiredValueInCents }) =>
        priceCell({ valueInCents: committedCostInCents, desiredValueInCents }),
    }),
    column<CommittedAtStartTableRow>({
      header: emptyCell,
      totals: emptyCell,
      sellableProject: (p) => (
        <Button
          variant="text"
          label="Release"
          onClick={createReleaseToTradesConfirmUrl(p.id)}
          disabled={p.currentCommittedCostInBasisPoints === 100_00}
        />
      ),
      projectCostCode: emptyCell,
      commitment: emptyCell,
      changeOrder: emptyCell,
      clientSideSort: false,
    }),
  ];
}

function createRows(
  data: CommittedAtStartTabMappedProjectCostCode[],
  collapsedProjectCostCodeData: CommittedAtStartTab_ProjectCostCodeResponse,
  showReleased: boolean,
  commitmentStatusFilter: CommitmentStatus[] | undefined,
): GridDataRow<CommittedAtStartTableRow>[] {
  const totalProjectCount = data.length;

  const projectRows = data.map(({ project, projectCostCodes }) => ({
    kind: "sellableProject" as const,
    id: project.id,
    initCollapsed: true,
    data: {
      ...project,
      currentCommittedCostInBasisPoints: Math.round(
        (project.committedCostInCents / project.directCostInCents) * 100_00,
      ),
    },
    children: projectCostCodes
      .map((pcc) => mapProjectCostCodeToRow(pcc, collapsedProjectCostCodeData, commitmentStatusFilter))
      .filter((pccRow) => {
        // Filter out Released cost codes if the filter is applied
        if (!showReleased && pccRow.data.status === CostCodeCommittedStatus.Released) return false;
        return true;
      }),
  })) as GridDataRow<SellableProjectRow>[];

  const totalCommittedAtStartInBasisPoints = projectRows.sum((p) => p.data.committedCostCodesAtStartInBasisPoints ?? 0);
  const totalCurrentCommittedInBasisPoints = projectRows.sum((p) => p.data.currentCommittedCostInBasisPoints);
  const totalRow = {
    kind: "totals",
    id: "totals",
    data: {
      committedAtStartInBasisPoints: totalCommittedAtStartInBasisPoints / totalProjectCount,
      currentCommittedCostInBasisPoints: totalCurrentCommittedInBasisPoints / totalProjectCount,
    },
  } as GridDataRow<TotalsRow>;

  return [simpleHeader, totalRow, ...projectRows];
}

function mapProjectCostCodeToRow(
  pcc: CommittedAtStartTab_ProjectCostCodeFragment,
  collapsedProjectCostCodeData: CommittedAtStartTab_ProjectCostCodeResponse,
  commitmentStatusFilter: CommitmentStatus[] | undefined,
): GridDataRow<ProjectCostCodeRow> {
  const commitmentLikeRows = collapsedProjectCostCodeData.data?.commitments.nonEmpty
    ? mapCommitmentLikeRows(collapsedProjectCostCodeData, pcc).filter(
        (cLikeRow) =>
          !commitmentStatusFilter ||
          commitmentStatusFilter.isEmpty ||
          commitmentStatusFilter.includes(cLikeRow.data.statusDetail.code),
      )
    : // By default, return one empty commitment row to display the collapse toggle
      [{ kind: "commitmentLike", id: `sample-clike-${pcc.id}`, initCollapsed: true, data: { name: "" } }];

  return {
    kind: "projectCostCode",
    id: pcc.id,
    initCollapsed: true,
    data: { ...pcc, status: projectCostCodeCommittedStatus(pcc) },
    children: commitmentLikeRows,
  } as GridDataRow<ProjectCostCodeRow>;
}

function mapCommitmentLikeRows(
  data: CommittedAtStartTab_ProjectCostCodeResponse,
  pcc: CommittedAtStartTab_ProjectCostCodeFragment,
): GridDataRow<CommitmentRow>[] {
  const mapData = (
    cLike: CommittedAtStartTab_CommitmentsByProjectCostCode_CommitmentLikeFragment,
    projectId: string,
  ) => {
    // Calculate the committed cost in cents for the specific cost code
    const committedCostCodeInCents = cLike.lineItems.sum((cli) =>
      cli.costCode.id === pcc.costCode.id ? cli.committedInCents : 0,
    );
    return {
      name: cLike.shortName,
      statusDetail: { code: cLike.status, name: cLike.statusText },
      desiredValueInCents: committedCostCodeInCents,
      projectId,
      // Return $0 on current committed cost if the commitment is not signed
      committedCostInCents: cLike.status === CommitmentStatus.Signed ? committedCostCodeInCents : 0,
    };
  };

  const projectCostCodeCommitments = data.data?.commitments
    .filter((c) => c.project.id === pcc.project.id)
    .filter((c) => c.lineItems.some((cli) => cli.costCode.id === pcc.costCode.id));

  return (projectCostCodeCommitments ?? []).map((commitment) => ({
    kind: "commitment",
    id: commitment.id,
    data: mapData(commitment, commitment.project.id),
    children: commitment.changeOrders.map((changeOrder) => ({
      kind: "changeOrder",
      id: changeOrder.id,
      data: mapData(changeOrder, commitment.project.id),
    })),
  })) as GridDataRow<CommitmentRow>[];
}

function percentageCellInBasisPoints(percentageInBasisPoint: number) {
  return percentageCell(percentageInBasisPoint, { basisPoints: true, desiredPercentage: 95_00 });
}

const costCodeCommittedStatusStyleMapper: Record<CostCodeCommittedStatus, DotIndicatorProps> = {
  [CostCodeCommittedStatus.Missing]: {
    bgColor: Palette.Red50,
    dotColor: Palette.Red500,
    label: CostCodeCommittedStatus.Missing,
    textColor: Palette.Gray800,
  },
  [CostCodeCommittedStatus.PartiallyReleased]: {
    bgColor: Palette.Yellow50,
    dotColor: Palette.Yellow500,
    label: CostCodeCommittedStatus.PartiallyReleased,
    textColor: Palette.Gray800,
  },
  [CostCodeCommittedStatus.Released]: {
    bgColor: Palette.Green50,
    dotColor: Palette.Green500,
    label: CostCodeCommittedStatus.Released,
    textColor: Palette.Gray800,
  },
  [CostCodeCommittedStatus.Unknown]: {
    bgColor: Palette.Red50,
    dotColor: Palette.Red500,
    label: CostCodeCommittedStatus.Unknown,
    textColor: Palette.Gray800,
  },
};

function projectCostCodeCommittedStatus(pcc: CommittedAtStartTab_ProjectCostCodeFragment) {
  if (pcc.committedCostInCents === 0) return CostCodeCommittedStatus.Missing;
  if (pcc.committedCostInCents < pcc.revisedCostInCents) return CostCodeCommittedStatus.PartiallyReleased;
  if (pcc.committedCostInCents === pcc.revisedCostInCents) return CostCodeCommittedStatus.Released;

  return CostCodeCommittedStatus.Unknown;
}

const commitmentStatusDotIndicatorStyleMapper: Record<CommitmentStatus, DotIndicatorProps> = {
  [CommitmentStatus.Draft]: {
    bgColor: Palette.Yellow50,
    dotColor: Palette.Yellow500,
    label: CommitmentStatus.Draft,
    textColor: Palette.Gray800,
  },
  [CommitmentStatus.Sent]: {
    bgColor: Palette.Yellow50,
    dotColor: Palette.Yellow500,
    label: CommitmentStatus.Sent,
    textColor: Palette.Gray800,
  },
  [CommitmentStatus.PartiallySigned]: {
    bgColor: Palette.Yellow50,
    dotColor: Palette.Yellow500,
    label: CommitmentStatus.PartiallySigned,
    textColor: Palette.Gray800,
  },
  [CommitmentStatus.Uploaded]: {
    bgColor: Palette.Yellow50,
    dotColor: Palette.Yellow500,
    label: CommitmentStatus.Uploaded,
    textColor: Palette.Gray800,
  },
  [CommitmentStatus.Voided]: {
    bgColor: Palette.Red50,
    dotColor: Palette.Red500,
    label: CommitmentStatus.Voided,
    textColor: Palette.Gray800,
  },
  [CommitmentStatus.Signed]: {
    bgColor: Palette.Green50,
    dotColor: Palette.Green500,
    label: CommitmentStatus.Signed,
    textColor: Palette.Gray800,
  },
};

function commitmentLikeStatusCell(commitmentLikeRow: CommitmentLikeRowData) {
  return (
    <DotIndicator
      {...commitmentStatusDotIndicatorStyleMapper[commitmentLikeRow.statusDetail.code]}
      label={commitmentLikeRow.statusDetail.name}
    />
  );
}

function projectCostCodeCommittedStatusCell(pcc: ProjectCostCodeRow["data"]) {
  return <DotIndicator {...costCodeCommittedStatusStyleMapper[pcc.status]} label={pcc.status} />;
}
